import { Roles } from "../../constants/roles";
import { FormBase } from "../../util/form-base-form";
import { toPositiveIntOrNull } from "../../util/id-utils";

export class UserProfileTabsForm extends FormBase {
    public panelsRoot: HTMLElement;

    public layoutRoot: Element | null;

    public tabsRoot: Element | null;

    public constructor(panelsRoot: HTMLElement) {
        super();
        this.panelsRoot = panelsRoot;
        this.layoutRoot = this.closest(panelsRoot, `[data-role="${Roles.USER_PROFILE_TABS_ROOT}"]`, HTMLElement);
        this.tabsRoot = this.layoutRoot
            ? this.queryElement(this.layoutRoot, `[data-role="${Roles.USER_PROFILE_TABS}"]`, HTMLElement)
            : null;
    }

    private queryRole<T extends Element>(root: ParentNode, role: string): T | null {
        return this.queryElement<T>(root, `[data-role="${role}"]`);
    }

    private queryAllRoles<T extends Element>(root: ParentNode, role: string): T[] {
        return this.queryElements<T>(root, `[data-role="${role}"]`);
    }

    public getPanelsRoot(): HTMLElement {
        return this.panelsRoot;
    }

    public getProfileBody(): HTMLElement | null {
        const layoutRoot = this.getLayoutRoot();
        if (!(layoutRoot instanceof HTMLElement)) return null;
        return this.closest(layoutRoot, ".profile-body", HTMLElement);
    }

    public getLayoutRoot(): HTMLElement | null {
        return this.layoutRoot instanceof HTMLElement ? this.layoutRoot : null;
    }

    public getTabsRoot(): HTMLElement | null {
        return this.tabsRoot instanceof HTMLElement ? this.tabsRoot : null;
    }

    public isInitialized(): boolean {
        const layoutRoot = this.getLayoutRoot();
        return layoutRoot instanceof HTMLElement ? layoutRoot.dataset.profileTabsBound === "1" : false;
    }

    public markInitialized(): void {
        const layoutRoot = this.getLayoutRoot();
        if (layoutRoot instanceof HTMLElement) {
            layoutRoot.dataset.profileTabsBound = "1";
        }
    }

    public getUserId(): number {
        return toPositiveIntOrNull(this.panelsRoot.dataset.userId) ?? 0;
    }

    public getDefaultTab(): string {
        const tabsRoot = this.getTabsRoot();
        const firstTab = this.getFirstTab();
        if (!(tabsRoot instanceof HTMLElement)) return firstTab;

        const activeTab = tabsRoot.getAttribute("data-active-tab") || "";
        if (activeTab && this.hasTab(activeTab)) {
            return activeTab;
        }

        return firstTab;
    }

    public getFirstTab(): string {
        const firstButton = this.getTabButtons()[0];
        const firstTab = firstButton instanceof HTMLElement ? firstButton.dataset.tab || "" : "";
        return firstTab || "basic";
    }

    public getHashTab(): string {
        if (typeof window === "undefined") return "";
        const raw = typeof window.location?.hash === "string" ? window.location.hash : "";
        return raw.startsWith("#") ? raw.slice(1).trim() : "";
    }

    public hasTab(tab: string): boolean {
        if (!tab) return false;
        if (this.getTabButtons().some((button) => button instanceof HTMLElement && button.dataset.tab === tab)) {
            return true;
        }
        // Some tabs (e.g. "password") are reachable only via the Personal
        // Information dropdown, not a tab-slider button — a panel with no
        // matching button is still a valid tab to activate.
        return this.queryAllRoles<HTMLElement>(this.panelsRoot, Roles.USER_PROFILE_TAB_PANEL).some(
            (panel) => panel instanceof HTMLElement && panel.dataset.tabPanel === tab,
        );
    }

    public getTabButtons(): HTMLElement[] {
        const tabsRoot = this.getTabsRoot();
        if (!(tabsRoot instanceof HTMLElement)) return [];
        return this.queryAllRoles<HTMLElement>(tabsRoot, Roles.USER_PROFILE_TAB_BUTTON);
    }

    public getTabSliderViewport(): HTMLElement | null {
        const tabsRoot = this.getTabsRoot();
        if (!(tabsRoot instanceof HTMLElement)) return null;
        return this.queryElement(tabsRoot, ".tab-slider__viewport", HTMLElement);
    }

    public closestTabButton(target: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(target, `[data-role="${Roles.USER_PROFILE_TAB_BUTTON}"]`, HTMLElement);
    }

    public getStatTriggers(): HTMLElement[] {
        const layoutRoot = this.getLayoutRoot();
        if (!(layoutRoot instanceof HTMLElement)) return [];
        return this.queryAllRoles<HTMLElement>(layoutRoot, Roles.USER_PROFILE_TAB_TRIGGER);
    }

    public getPointHistoryPanel(): HTMLElement | null {
        return this.queryElement(
            this.panelsRoot,
            `[data-role="${Roles.USER_PROFILE_TAB_PANEL}"][data-tab-panel="point-history"]`,
            HTMLElement,
        );
    }

    public getPointHistoryIncludeInvalidInput(): HTMLInputElement | null {
        const panel = this.getPointHistoryPanel();
        if (!(panel instanceof HTMLElement)) return null;
        const el = this.queryRole<HTMLInputElement>(panel, Roles.POINT_HISTORY_INCLUDE_INVALID);
        return el instanceof HTMLInputElement ? el : null;
    }

    public getRoleTrigger(role: string): HTMLElement | null {
        const layoutRoot = this.getLayoutRoot();
        if (!(layoutRoot instanceof HTMLElement)) return null;
        return this.queryElement(layoutRoot, `[data-role="${role}"]`, HTMLElement);
    }

    public getTabList(tab: string): HTMLElement | null {
        const tabListRoleMap: Record<string, string> = {
            followers: Roles.FOLLOWERS_LIST,
            followings: Roles.FOLLOWINGS_LIST,
        };
        const role = tabListRoleMap[tab] || Roles.USER_PROFILE_TAB_LIST;
        return this.queryElement(this.panelsRoot, `[data-role="${role}"][data-tab-list="${tab}"]`, HTMLElement);
    }

    public hasSsrTabContent(root: ParentNode, tab: string): boolean {
        const selector =
            tab === "posts"
                ? '.board-card, [data-role="post-card"]'
                : tab === "favorites"
                  ? ".product-card"
                  : tab === "followers"
                    ? ".user-card-slide, .user-card"
                    : ".user-card-slide";
        return this.queryElement(root, selector, HTMLElement) instanceof HTMLElement;
    }

    public getPointHistoryTotal(): HTMLElement | null {
        return this.queryElement(this.panelsRoot, `[data-role="${Roles.POINT_HISTORY_PANEL_TOTAL}"]`, HTMLElement);
    }

    public getTabCountElements(tab: string): HTMLElement[] {
        const layoutRoot = this.getLayoutRoot();
        if (!(layoutRoot instanceof HTMLElement)) return [];
        return this.queryElements(
            layoutRoot,
            `[data-role="${Roles.USER_PROFILE_TAB_COUNT}"][data-tab-count="${tab}"]`,
            HTMLElement,
        );
    }

    public getFavoriteCard(root: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.USER_PROFILE_FAVORITE_CARD}"]`, HTMLElement);
    }

    public getProductLikeButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null {
        return this.closest(root, `[data-role="${Roles.PRODUCT_LIKE_BTN}"]`, HTMLButtonElement);
    }

    public getFollowingCard(root: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.USER_PROFILE_FOLLOWING_CARD}"]`, HTMLElement);
    }

    public getTabRow(root: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.USER_PROFILE_TAB_ROW}"]`, HTMLElement);
    }

    public getFavoritesList(root: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(
            root,
            `[data-role="${Roles.USER_PROFILE_TAB_LIST}"][data-tab-list="favorites"]`,
            HTMLElement,
        );
    }

    public getFollowingsList(root: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.FOLLOWINGS_LIST}"][data-tab-list="followings"]`, HTMLElement);
    }

    public hasTabRows(root: ParentNode): boolean {
        return (
            this.queryElement(root, `[data-role="${Roles.USER_PROFILE_TAB_ROW}"]`, HTMLElement) instanceof HTMLElement
        );
    }

    public hasFollowingCards(root: ParentNode): boolean {
        return (
            this.queryElement(root, `[data-role="${Roles.USER_PROFILE_FOLLOWING_CARD}"]`, HTMLElement) instanceof
            HTMLElement
        );
    }

    public getRenderedProductCard(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".post-product-linker__card", HTMLElement);
    }
}
